import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import tokens from '../../assets/styles/tokens';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';

const ReservedTargetOrdersCardContainer = styled.div`
  border-radius: ${variables.borderRadius.borderRadiusMdPlus};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: 0px 3px 9px 0px ${themeColors.colorShadowCard};
  border: 1px solid ${themeColors.colorWhite};
  padding: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(16)} ${pxToRemMd(16)} ${pxToRemMd(12)};
  }
`;

const ReservedTargetOrdersHeader = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: ${pxToRem(12.5)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  margin-bottom: ${pxToRem(11.5)};

  // The title wraps onto its own multiple lines (see ReservedTargetOrdersTitle's
  // max-width below), which is enough on its own to free up room for the
  // amount — keep the row itself nowrap the whole way down from bpXl so the
  // amount never gets pushed below the title (it did right at/near bpMd
  // before, when this was wrap here and only forced back to nowrap below the
  // narrower bpMd).
  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding-bottom: ${pxToRemMd(12)};
    margin-bottom: ${pxToRemMd(11)};
    flex-wrap: nowrap;
  }
`;

const ReservedTargetOrdersTitle = styled.p`
  ${tokens.type.label[400]}
  color: ${themeColors.colorTextSecondary};
  margin-right: auto;
  padding-right: ${pxToRem(10)};
  min-width: 0;

  // Letting the title merely shrink-to-fit (flex: 1 1 auto) only wraps it
  // once the row is genuinely out of room, which at these widths it isn't —
  // "Reserved for Buy Target Orders" comfortably fits next to the amount at
  // 18px until the row gets fairly narrow. Capping max-width forces the wrap
  // unconditionally the whole way down from bpXl (not just below the
  // narrower bpMd), which also keeps the header row itself from ever needing
  // to wrap (see ReservedTargetOrdersHeader).
  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex: 1 1 auto;
    max-width: 60%;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
  }
`;

const ReservedTargetOrdersAmount = styled.p`
  ${tokens.type.title[400]}
  text-align: right;
  color: ${themeColors.colorTextPrimary};
  // Without this, a large enough amount (long enough that it + the title
  // don't both fit on the header row) can wrap onto its own second line by
  // default flex/text behavior — keep it on one line and let the title (which
  // can wrap) give way instead, at every breakpoint, not just mobile.
  flex-shrink: 0;
  white-space: nowrap;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    margin-left: auto;
  }
`;

const ReservedTargetOrdersDescription = styled.p`
  ${tokens.type.body[200]}
  color: ${tokens.color.text.label};
  margin: 0;
`;

const ReservedTargetOrdersCard = ({ title, balance, description }) => (
  <ReservedTargetOrdersCardContainer>
    <ReservedTargetOrdersHeader>
      <ReservedTargetOrdersTitle>{title}</ReservedTargetOrdersTitle>
      <ReservedTargetOrdersAmount>
        {formatMoneyNumeral(balance)}
      </ReservedTargetOrdersAmount>
    </ReservedTargetOrdersHeader>
    <ReservedTargetOrdersDescription>
      {description}
    </ReservedTargetOrdersDescription>
  </ReservedTargetOrdersCardContainer>
);

ReservedTargetOrdersCard.propTypes = {
  title: PropTypes.string,
  balance: PropTypes.number,
  description: PropTypes.string,
};

export default ReservedTargetOrdersCard;
